import classNames from "classnames";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { useBreakpoint } from "~/contexts/breakpoint";
import { useWishlist } from "~/contexts/wishlist";
import { useCartProduct } from "~/hooks/use-cart";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Button from "../common/button";
import CartProductPriceLater from "./cart-product-price-later";
import styles from "./cart-product-tile.module.scss";
import CartWishlistDialog from "./cart-wishlist-dialog";

export type Product = NonNullable<EVA.Core.SearchProductsResponse["Products"]>[number];

type Props = {
  line: any;
  itemIndex: number;
  outOfStockDelivery?: boolean;
  outOfStockPickup?: boolean;
  onRemove: (prodId: number, productName: string, itemIndex: number) => void;
  movedToWishlist: (prodId: number, productName: string, destinationListId: number | string, itemIndex: number) => void;
  rootProductDetails?: any;
};

export default function CartProductTileLater(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();

  const trackingInfo = {
    context: "saved-for-later",
    info: TrackingUtils.mapEcomCartItem(props.line, props.rootProductDetails),
  };
  const { addToCart } = useCartProduct(props.line.product_id, undefined, trackingInfo);
  const { refetch, savedForLaterList, removeProdFromWishlist } = useWishlist();
  const { outOfStockDelivery = false } = props;

  const [openWishlistSelector, setOpenWishlistSelector] = useState(false);

  const product = props.line;
  const primaryImage = props.line.product_media?.primary_image;

  const color = props.line.color;
  const goccia = productUtils.getProductDroplet(props.line.product_media?.media);

  const backToYourBagHandler = async (product: any): Promise<void> => {
    const id: number = product.product_id;
    if (productUtils.hasValidDisplayPrice(product)) {
      await addToCart.mutateAsync();
      if (savedForLaterList) {
        removeProdFromWishlist(id, savedForLaterList.ID).then(() => {
          refetch();
        });
      }
    }
  };

  return (
    <div className={classNames(styles.container, { [styles.cancelledItem]: props.line.IsCancelled })}>
      <Image src={primaryImage?.url} alt={product?.display_value} width={80} height={80} />
      <div className={styles.infoContainer}>
        <p className={styles.productName}>{product?.display_value}</p>
        {goccia && color ? (
          <div className={styles.colorContainer}>
            <Image
              src={goccia?.url}
              alt={color}
              width={12}
              height={12}
              className={styles.colorImage}
              loader={imageUtils.evaImageLoader}
            />
            <p className={styles.colorName}>{color}</p>
          </div>
        ) : (
          <div className={styles.colorPlaceholder} />
        )}
        <div className={styles.bottomContainer}>
          {!props.line.CustomOrderLineType && breakpoint === "desktop" && (
            <div className={styles.actionsContainer}>
              {!outOfStockDelivery ? (
                <Button
                  disabled={!productUtils.hasValidDisplayPrice(product)}
                  aria-disabled={!productUtils.hasValidDisplayPrice(product)}
                  className={styles.moveToBag}
                  variant="secondary"
                  onClick={() => backToYourBagHandler(product)}
                >
                  {t("generic.move_into_your_bag")}
                </Button>
              ) : null}
              <button
                className={styles.button}
                onClick={() => props.onRemove(props.line.product_id, props.line.display_value, props.itemIndex)}
              >
                {t("generic.remove")}
              </button>
              <button className={styles.button} onClick={() => setOpenWishlistSelector(true)}>
                {t("generic.move_to_wishlist")}
              </button>
            </div>
          )}
          <CartProductPriceLater line={props.line} />
        </div>
        {breakpoint === "mobile" && (
          <Button
            className={styles.moveToBag}
            variant="secondary"
            disabled={!productUtils.hasValidDisplayPrice(product)}
            aria-disabled={!productUtils.hasValidDisplayPrice(product)}
            onClick={() => backToYourBagHandler(product)}
            style={{ width: "max-content" }}
          >
            {t("generic.move_into_your_bag")}
          </Button>
        )}
        {breakpoint === "mobile" && (
          <div className={styles.mobileActions}>
            <button
              className={styles.button}
              style={{ alignSelf: "flex-start" }}
              onClick={() => props.onRemove(props.line.product_id, props.line.display_value, props.itemIndex)}
            >
              {t("generic.remove")}
            </button>
            <button className={styles.button} onClick={() => setOpenWishlistSelector(true)}>
              {t("generic.move_to_wishlist")}
            </button>
          </div>
        )}
      </div>
      <CartWishlistDialog
        productId={props.line.product_id}
        isOpen={openWishlistSelector}
        onClose={(destinationListId) => {
          setOpenWishlistSelector(false);
          if (destinationListId) {
            props.movedToWishlist(props.line.product_id, props.line.display_value, destinationListId, props.itemIndex);
          }
        }}
        savedForLater
      />
    </div>
  );
}
